import React, { useEffect, useState } from 'react';
import API from '../api';
import Navbar from '../components/Navbar';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [returnQty, setReturnQty] = useState({});
  const [search, setSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [products, setProducts] = useState([]);
  const [showAddMore, setShowAddMore] = useState(false);
  const [addMoreItems, setAddMoreItems] = useState([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
  const [addMoreSearches, setAddMoreSearches] = useState(['']);
  const [showAddMoreDropdown, setShowAddMoreDropdown] = useState({});

  const fetchInvoices = async () => {
    try {
      const res = await API.get('/invoices');
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await API.get('/products');
      setProducts(res.data);
    } catch (err) {
      console.error(err);
    }
  };
  const handleProductSearch = async () => {
    if (!search.trim()) {
      setProductSearchResults([]);
      return;
    }
    setSearchingProducts(true);
    try {
      const res = await API.get(`/invoices/search-by-product?query=${encodeURIComponent(search)}`);
      setProductSearchResults(res.data);
    } catch (err) {
      console.error(err);
      setProductSearchResults([]);
    }
    setSearchingProducts(false);
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchInvoices();
    fetchProducts();
    const openId = searchParams.get('open');
    if (openId) {
      handleViewInvoice(parseInt(openId));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim()) {
        handleProductSearch();
      } else {
        setProductSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const handleViewInvoice = async (id) => {
    setLoading(true);
    setReturnQty({});
    setShowAddMore(false);
    setSettleMode(false);
    setAlreadyPaidInput('');
    setAddMoreItems([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
    setAddMoreSearches(['']);
    try {
      const res = await API.get(`/invoices/${id}`);
      setSelectedInvoice(res.data);
    } catch (err) {
      toast.error('Failed to load invoice');
    }
    setLoading(false);
  };

  const [processingReturn, setProcessingReturn] = useState(false);

  const handleReturnItem = async (invoiceId, itemId) => {
    if (processingReturn) return;
    const qty = returnQty[itemId];
    if (!qty || qty < 1) {
      toast.error('Please enter return quantity');
      return;
    }
    if (!window.confirm(`Return ${qty} item(s)?`)) return;
    setProcessingReturn(true);
    try {
      await API.put(`/invoices/${invoiceId}/return-item`, {
        item_id: itemId,
        return_quantity: parseInt(qty),
      });
      toast.success('Item returned successfully!');
      handleViewInvoice(invoiceId);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to return item');
    }
    setProcessingReturn(false);
  };

  const handleReturnAll = async (invoiceId) => {
    if (processingReturn) return;
    if (!window.confirm('Return ALL items in this invoice? Stock will be fully restored.')) return;
    setProcessingReturn(true);
    try {
      await API.put(`/invoices/${invoiceId}/return-all`);
      toast.success('All items returned successfully!');
      handleViewInvoice(invoiceId);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to return all items');
    }
    setProcessingReturn(false);
  };



const [openMenuId, setOpenMenuId] = useState(null);
const [settleMode, setSettleMode] = useState(false);
const [alreadyPaidInput, setAlreadyPaidInput] = useState('');
  const handleTogglePin = async (invoiceId) => {
    try {
      const res = await API.put(`/invoices/${invoiceId}/toggle-pin`);
      toast.success(res.data.pinned ? 'Invoice pinned — will never auto-delete' : 'Invoice unpinned');
      fetchInvoices();
    } catch (err) {
      toast.error('Failed to update pin');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this fully-returned invoice now? This cannot be undone.')) return;
    try {
      await API.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      if (selectedInvoice?.id === invoiceId) setSelectedInvoice(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const [submittingAddMore, setSubmittingAddMore] = useState(false);

  const handleAddMoreItems = async () => {
    if (submittingAddMore) return;
    for (const item of addMoreItems) {
      if (!item.product_id) {
        toast.error('Please select a product for all items');
        return;
      }
    }
    setSubmittingAddMore(true);
    try {
      const res = await API.put(`/invoices/${selectedInvoice.id}/add-items`, {
        items: addMoreItems.map(item => ({
          ...item,
          actual_price: item.actual_price > 0 ? item.actual_price : item.price,
        })),
      });
      toast.success('Items added to invoice!');
      await fetchProducts();
      handleViewInvoice(selectedInvoice.id);
      fetchInvoices();
      setShowAddMore(false);
      setAddMoreItems([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
      setAddMoreSearches(['']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add items');
    }
    setSubmittingAddMore(false);
  };

  const handlePrint = () => window.print();

  const getStatusStyle = (status) => {
    if (status === 'returned') return styles.badgeReturned;
    if (status === 'partial_return') return styles.badgePartial;
    return styles.badge;
  };

  const getStatusLabel = (status) => {
    if (status === 'returned') return 'Returned';
    if (status === 'partial_return') return 'Partial Return';
    return 'Paid';
  };

  const filteredInvoices = invoices.filter((inv) =>
    inv.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    (inv.invoice_number && inv.invoice_number.includes(search)) ||
    inv.id.toString().includes(search)
  );

  return (
    <div>
      <Navbar />
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable, #printable * { visibility: visible; }
          #printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print { display: none !important; }
          #printable table { width: 100%; border-collapse: collapse; }
          #printable th { background: #f5f3ff !important; -webkit-print-color-adjust: exact; }
          @page { margin: 0; size: auto; }
        }
      `}</style>
      <div style={styles.container} className="container">
        <div style={styles.header} className="no-print">
          <h2 style={styles.title}>Invoices</h2>
          <Link to="/invoices/create" style={styles.button}>+ New Invoice</Link>
        </div>
        <div style={styles.smartSearchBox} className="no-print">
          <input
            style={styles.search}
            type="text"
            placeholder="Search by customer name, invoice number, product name or barcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleProductSearch()}
          />

          {productSearchResults.length > 0 && (
            <div style={styles.productResultsBox}>
              <p style={{ fontSize: '13px', color: '#4f46e5', fontWeight: '600', marginBottom: '0.5rem' }}>
                🔍 Also found in products/barcodes — {productSearchResults.length} invoice(s):
              </p>
              {productSearchResults.map((inv) => (
                <div
                  key={inv.id}
                  onClick={() => { handleViewInvoice(inv.id); setProductSearchResults([]); }}
                  style={styles.productResultItem}
                >
                  <div>
                    <span style={{ fontWeight: '600' }}>#{inv.invoice_number || inv.id}</span>
                    <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>
                      {inv.matched_products?.join(', ')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#888' }}>{new Date(inv.created_at).toLocaleDateString()}</span>
                    <span style={{ fontWeight: '600', color: '#4f46e5' }}>LKR {parseFloat(inv.total_amount).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={styles.layout} className="layout">
<div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table} className="no-print">            <thead>
              <tr>
                <th style={styles.th}>Invoice #</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Amount (LKR)</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ ...styles.td, textAlign: 'center', color: '#888' }}>No invoices found</td>
                </tr>
              ) : (
                filteredInvoices.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => handleViewInvoice(inv.id)}
                    style={{ ...styles.row, background: selectedInvoice?.id === inv.id ? '#e0e7ff' : '#fff' }}
                  >
                    <td style={styles.td}>#{inv.invoice_number || inv.id}</td>
                    <td style={styles.td}>{inv.customer_name}</td>
                    <td style={styles.td}>{parseFloat(inv.total_amount).toFixed(2)}</td>
                    <td style={styles.td}>
                      <span style={getStatusStyle(inv.status)}>{getStatusLabel(inv.status)}</span>
                    </td>
                    <td style={{ ...styles.td, position: 'relative' }}>
                      {new Date(inv.created_at).toLocaleDateString()}
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === inv.id ? null : inv.id); }}
                        style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#888', fontWeight: 'bold' }}
                      >
                        ⋮
                      </button>
                      {openMenuId === inv.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{ position: 'absolute', right: 0, top: '100%', zIndex: 10, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '170px', overflow: 'hidden' }}
                        >
                          <button
                            onClick={() => { handleTogglePin(inv.id); setOpenMenuId(null); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}
                          >
                            {inv.pinned ? 'Unpin' : 'Pin (prevent auto-delete)'}
                          </button>
                          {inv.status === 'returned' && !inv.pinned && (
                            <button
                              onClick={() => { handleDeleteInvoice(inv.id); setOpenMenuId(null); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ef4444' }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table></div>

          {loading && <div style={styles.detail}><p>Loading...</p></div>}

          {selectedInvoice && !loading && (
            <div style={styles.detail} id="printable">
              <div style={styles.billHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                  {JSON.parse(localStorage.getItem('shopProfile') || '{}')?.logo_url && (
                    <img src={JSON.parse(localStorage.getItem('shopProfile') || '{}').logo_url} alt="Logo" style={{ maxHeight: '40px', maxWidth: '80px' }} />
                  )}
<h3 style={{ ...styles.detailTitle, margin: 0 }}>{JSON.parse(localStorage.getItem('user'))?.shop_name}</h3>                </div>
                {JSON.parse(localStorage.getItem('shopProfile'))?.address && (
                  <p style={styles.detailText}>📍 {JSON.parse(localStorage.getItem('shopProfile'))?.address}</p>
                )}
                {JSON.parse(localStorage.getItem('shopProfile'))?.phone && (
                  <p style={styles.detailText}>📞 {JSON.parse(localStorage.getItem('shopProfile'))?.phone}</p>
                )}
                {JSON.parse(localStorage.getItem('shopProfile'))?.whatsapp && (
                  <p style={styles.detailText}>💬 WhatsApp: {JSON.parse(localStorage.getItem('shopProfile'))?.whatsapp}</p>
                )}
                <div style={styles.billDividerTop}></div>
                <p style={styles.detailText}>Invoice #{selectedInvoice.invoice_number || selectedInvoice.id}</p>
         <p style={styles.detailText} className="no-print">Customer: <strong>{selectedInvoice.customer_name}</strong></p>                <p style={styles.detailText}>Date: {new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
<p style={styles.detailText} className="no-print">Status: <span style={getStatusStyle(selectedInvoice.status)}>{getStatusLabel(selectedInvoice.status)}</span></p>              </div>

              <table style={styles.itemTable}>
                <thead>
                  <tr>
                    <th style={styles.ith}>Product</th>
                    <th style={styles.ith}>Bought</th>
                    <th style={styles.ith}>Returned</th>
                    <th style={styles.ith}>Final Qty</th>
                    <th style={styles.ith}>Price</th>
                    <th style={styles.ith}>Total</th>
                    <th style={styles.ith} className="no-print">Return</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items?.map((item, i) => {
                    const originalPrice = parseFloat(item.price);
                    const actualPrice = parseFloat(item.actual_price) > 0 ? parseFloat(item.actual_price) : originalPrice;
                    const isOffer = actualPrice < originalPrice;
                    const returnedQty = item.returned_quantity || 0;
                    const finalQty = item.quantity - returnedQty;
                    const finalTotal = actualPrice * finalQty;
                    const availableToReturn = item.quantity - returnedQty;
                    return (
                      <tr key={i}>
                        <td style={styles.itd}>
                          {item.name}
                          {isOffer && (
                            <span style={{ display: 'block', fontSize: '11px', color: '#16a34a', fontWeight: 'bold' }}>
                              🏷️ Special Price
                            </span>
                          )}
                        </td>
                        <td style={styles.itd}>{item.quantity}</td>
                        <td style={{ ...styles.itd, color: returnedQty > 0 ? '#ef4444' : 'inherit' }}>
                          {returnedQty > 0 ? `-${returnedQty}` : 0}
                        </td>
                        <td style={{ ...styles.itd, fontWeight: 'bold' }}>{finalQty}</td>
                        <td style={styles.itd}>
                          {isOffer && (
                            <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '11px', display: 'block' }}>
                              LKR {originalPrice.toFixed(2)}
                            </span>
                          )}
                          <span style={{ color: isOffer ? '#16a34a' : 'inherit' }}>
                            LKR {actualPrice.toFixed(2)}
                          </span>
                        </td>
                        <td style={styles.itd}>LKR {finalTotal.toFixed(2)}</td>
                        <td style={styles.itd} className="no-print">
                          {availableToReturn > 0 ? (
                            <div style={styles.returnRow}>
                              <input
                                type="number"
                                min="1"
                                max={availableToReturn}
                                placeholder="Qty"
                                value={returnQty[item.id] || ''}
                                onChange={(e) => setReturnQty({ ...returnQty, [item.id]: e.target.value })}
                                style={styles.returnInput}
                              />
<button onClick={() => handleReturnItem(selectedInvoice.id, item.id)} style={{ ...styles.returnBtn, opacity: processingReturn ? 0.6 : 1 }} disabled={processingReturn}>Return</button>                            </div>
                          ) : (
                            <span style={{ color: '#ef4444', fontSize: '12px' }}>Returned</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {selectedInvoice.items?.some(item => (item.returned_quantity || 0) > 0) && (
                <div style={styles.returnSummary}>
                  <div style={styles.returnSummaryRow}>
                    <span>Original Amount:</span>
                    <span>LKR {selectedInvoice.items?.reduce((sum, item) => {
                      const actualPrice = parseFloat(item.actual_price) > 0 ? parseFloat(item.actual_price) : parseFloat(item.price);
                      return sum + actualPrice * item.quantity;
                    }, 0).toFixed(2)}</span>
                  </div>
                  <div style={styles.returnSummaryRow}>
                    <span style={{ color: '#ef4444' }}>Refund Amount:</span>
                    <span style={{ color: '#ef4444' }}>- LKR {selectedInvoice.items?.reduce((sum, item) => {
                      const actualPrice = parseFloat(item.actual_price) > 0 ? parseFloat(item.actual_price) : parseFloat(item.price);
                      return sum + actualPrice * (item.returned_quantity || 0);
                    }, 0).toFixed(2)}</span>
                  </div>
                  <div style={{ ...styles.returnSummaryRow, ...styles.returnSummaryFinal }}>
                    <span>Final Amount:</span>
                    <span style={{ color: '#4f46e5' }}>LKR {parseFloat(selectedInvoice.total_amount).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {!selectedInvoice.items?.some(item => (item.returned_quantity || 0) > 0) && (
                <div style={styles.total}>
                  <span>Total:</span>
                  <span style={styles.totalAmount}>LKR {parseFloat(selectedInvoice.total_amount).toFixed(2)}</span>
                </div>
              )}

              {settleMode && (
                <div className="no-print" style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '13px', display: 'block', marginBottom: '0.4rem' }}>Amount Already Paid by Customer:</label>
                  <input
                    type="number"
                    value={alreadyPaidInput}
                    onChange={(e) => setAlreadyPaidInput(e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              {settleMode && alreadyPaidInput !== '' && !isNaN(parseFloat(alreadyPaidInput)) && (
                <div style={styles.returnSummary}>
                  <div style={styles.returnSummaryRow}>
                    <span>Current Bill Amount:</span>
                    <span>LKR {parseFloat(selectedInvoice.total_amount).toFixed(2)}</span>
                  </div>
                  <div style={styles.returnSummaryRow}>
                    <span>Amount Already Paid:</span>
                    <span>LKR {parseFloat(alreadyPaidInput).toFixed(2)}</span>
                  </div>
                  <div style={{ ...styles.returnSummaryRow, ...styles.returnSummaryFinal }}>
                    <span>
                      {parseFloat(selectedInvoice.total_amount) > parseFloat(alreadyPaidInput)
                        ? 'Amount Due from Customer:'
                        : parseFloat(selectedInvoice.total_amount) < parseFloat(alreadyPaidInput)
                        ? 'Refund to Customer:'
                        : 'Fully Settled:'}
                    </span>
                    <span>LKR {Math.abs(parseFloat(selectedInvoice.total_amount) - parseFloat(alreadyPaidInput)).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div style={styles.billFooterSection}>
                <div style={styles.billDivider}></div>
                <p style={styles.billNote}>📋 <strong>Terms & Conditions:</strong></p>
                <ul style={styles.billTerms}>
                  <li>Products can be exchanged within <strong>7 days</strong> of purchase.</li>
                  <li>Items must be unused, undamaged and in original condition.</li>
                  <li>Please bring this receipt for any exchange or return.</li>
                  <li>No cash refund after purchase is confirmed.</li>
                </ul>
                <div style={styles.billDivider}></div>
                <p style={styles.billThankYou}>🙏 Thank you for shopping with us!</p>
                <p style={styles.billVisitAgain}>We look forward to seeing you again.</p>
              </div>

              {!settleMode && (
              <div style={styles.actions} className="no-print">
                <button onClick={handlePrint} style={styles.printBtn}>🖨️ Print Bill</button>
                {selectedInvoice.status !== 'returned' && (
                  <>
                    <button onClick={() => setShowAddMore(!showAddMore)} style={styles.addMoreBtn}>+ Add More Items</button>
<button onClick={() => handleReturnAll(selectedInvoice.id)} style={{ ...styles.returnAllBtn, opacity: processingReturn ? 0.6 : 1 }} disabled={processingReturn}>↩️ Return All</button>                  </>
                )}
              </div>
              )}

              {settleMode && (
                <div className="no-print" style={{ marginTop: '1rem' }}>
                  <button onClick={handlePrint} style={styles.printBtn}>Print Settlement</button>
                </div>
              )}

              {showAddMore && (
                <div style={styles.addMoreSection} className="no-print">
                  <h4 style={{ fontSize: '14px', marginBottom: '0.75rem' }}>Add More Items to Invoice #{selectedInvoice.invoice_number || selectedInvoice.id}</h4>
                  {addMoreItems.map((item, index) => (
                    <div key={index} style={styles.itemBlock}>
                      <div style={styles.searchWrapper}>
                        <input
                          style={styles.searchInput}
                          type="text"
                          placeholder="Search product..."
                          value={addMoreSearches[index]}
                          onChange={(e) => {
                            const s = [...addMoreSearches];
                            s[index] = e.target.value;
                            setAddMoreSearches(s);
                            setShowAddMoreDropdown({ ...showAddMoreDropdown, [index]: true });
                          }}
                          autoComplete="off"
                        />
                        {showAddMoreDropdown[index] && addMoreSearches[index] && (
                          <div style={styles.dropdown}>
                            {products
                              .filter((p) => (p.name.toLowerCase().includes(addMoreSearches[index].toLowerCase()) || (p.barcode && p.barcode.includes(addMoreSearches[index]))) && p.stock > 0)
                              .map((p) => (
                                <div key={p.id} style={styles.dropdownItem} onClick={() => {
                                  const alreadyUsedQty = addMoreItems.reduce((sum, itm, i) => {
                                    if (i !== index && itm.product_id === p.id) return sum + itm.quantity;
                                    return sum;
                                  }, 0);
                                  const availableStock = p.stock - alreadyUsedQty;
                                  if (availableStock <= 0) {
                                    toast.error(`No more stock available for ${p.name}!`);
                                    return;
                                  }
                                  const newItems = [...addMoreItems];
                                  newItems[index].product_id = p.id;
                                  newItems[index].price = parseFloat(p.selling_price);
                                  newItems[index].actual_price = parseFloat(p.selling_price);
                                  newItems[index].stock = availableStock;
                                  newItems[index].name = p.name;
                                  setAddMoreItems(newItems);
                                  const s = [...addMoreSearches];
                                  s[index] = p.name;
                                  setAddMoreSearches(s);
                                  setShowAddMoreDropdown({ ...showAddMoreDropdown, [index]: false });
                                }}>
                                  <span>{p.name}</span>
                                  <span style={{ fontSize: '12px', color: '#888' }}>
                                    LKR {parseFloat(p.selling_price).toFixed(2)} | Stock: {p.stock}
                                  </span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                      <div style={styles.itemRow}>
                        {item.product_id && (
                          <span style={{ fontSize: '13px', color: '#16a34a' }}>✅ {item.name} | Stock: {item.stock}</span>
                        )}
                        <input
                          style={styles.qtyInput}
                          type="number"
                          min="1"
                          max={item.stock}
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => {
                            const newItems = [...addMoreItems];
                            const qty = parseInt(e.target.value);
                            if (qty > item.stock) {
                              toast.error(`Only ${item.stock} available!`);
                              newItems[index].quantity = item.stock;
                            } else {
                              newItems[index].quantity = qty;
                            }
                            setAddMoreItems(newItems);
                          }}
                        />
                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#4f46e5' }}>
                          LKR {((item.actual_price || item.price) * item.quantity).toFixed(2)}
                        </span>
                        {addMoreItems.length > 1 && (
                          <button type="button" onClick={() => {
                            setAddMoreItems(addMoreItems.filter((_, i) => i !== index));
                            setAddMoreSearches(addMoreSearches.filter((_, i) => i !== index));
                          }} style={styles.removeItemBtn}>✕</button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button onClick={() => {
                    setAddMoreItems([...addMoreItems, { product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
                    setAddMoreSearches([...addMoreSearches, '']);
                  }} style={styles.addItemBtn}>+ Add Item</button>
<button onClick={handleAddMoreItems} style={{ ...styles.confirmAddBtn, opacity: submittingAddMore ? 0.6 : 1 }} disabled={submittingAddMore}>
                          {submittingAddMore ? 'Updating...' : 'Confirm & Update Invoice'}
                        </button>                </div>
              )}
              <div className="no-print" style={{ marginTop: '1rem' }}>
                <button onClick={() => { setSettleMode(!settleMode); setAlreadyPaidInput(''); setShowAddMore(false); }} style={{ padding: '8px 16px', background: settleMode ? '#e0e7ff' : '#fff', color: '#4f46e5', border: '1px solid #4f46e5', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', width: '100%' }}>
                  {settleMode ? 'Back to Invoice' : 'Settle Already-Paid Bill'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  title: { fontSize: '24px', margin: 0 },
  button: { padding: '10px 20px', background: '#4f46e5', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontSize: '14px' },
  search: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '1rem', boxSizing: 'border-box' },
  layout: { display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' },
  table: { flex: 2, minWidth: '300px', borderCollapse: 'collapse', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  th: { padding: '12px 16px', background: '#4f46e5', color: '#fff', textAlign: 'left', fontSize: '14px' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px', cursor: 'pointer' },
  row: { transition: 'background 0.2s' },
  badge: { padding: '4px 10px', background: '#d1fae5', color: '#065f46', borderRadius: '99px', fontSize: '12px' },
  badgeReturned: { padding: '4px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: '99px', fontSize: '12px' },
  badgePartial: { padding: '4px 10px', background: '#fef3c7', color: '#92400e', borderRadius: '99px', fontSize: '12px' },
  detail: { flex: 1, minWidth: '280px', background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  billHeader: { marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px dashed #ddd' },
  billDividerTop: { borderTop: '1px dashed #ddd', margin: '0.5rem 0' },
  detailTitle: { fontSize: '18px', marginBottom: '0.5rem' },
  detailText: { fontSize: '14px', marginBottom: '0.4rem' },
  itemTable: { width: '100%', borderCollapse: 'collapse', marginTop: '1rem' },
  ith: { padding: '8px', background: '#f5f3ff', fontSize: '13px', textAlign: 'left' },
  itd: { padding: '8px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' },
  returnSummary: { background: '#f5f3ff', borderRadius: '8px', padding: '1rem', margin: '1rem 0' },
  returnSummaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', padding: '4px 0' },
  returnSummaryFinal: { fontWeight: 'bold', fontSize: '16px', borderTop: '1px solid #ddd', marginTop: '8px', paddingTop: '8px' },
  total: { display: 'flex', justifyContent: 'space-between', padding: '1rem', background: '#f5f3ff', borderRadius: '8px', margin: '1rem 0', fontWeight: 'bold' },
  totalAmount: { color: '#4f46e5', fontSize: '18px' },
  billFooterSection: { marginTop: '1rem', textAlign: 'center' },
  billDivider: { borderTop: '1px dashed #ddd', margin: '0.75rem 0' },
  billNote: { fontSize: '12px', color: '#555', textAlign: 'left', marginBottom: '0.5rem' },
  billTerms: { fontSize: '12px', color: '#555', textAlign: 'left', paddingLeft: '1.2rem', lineHeight: '1.8' },
  billThankYou: { fontSize: '16px', fontWeight: 'bold', color: '#4f46e5', marginBottom: '4px' },
  billVisitAgain: { fontSize: '13px', color: '#888' },
  actions: { display: 'flex', gap: '1rem', marginTop: '1rem' },
  printBtn: { flex: 1, padding: '10px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  returnAllBtn: { flex: 1, padding: '10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  returnRow: { display: 'flex', gap: '4px', alignItems: 'center' },
  returnInput: { width: '50px', padding: '4px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' },
  returnBtn: { padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  addMoreBtn: { flex: 1, padding: '10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  addMoreSection: { marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #ddd' },
  itemBlock: { marginBottom: '0.75rem', padding: '0.75rem', background: '#f9f9f9', borderRadius: '8px', position: 'relative' },
  searchWrapper: { position: 'relative', marginBottom: '0.5rem' },
  searchInput: { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', zIndex: 100, maxHeight: '150px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  dropdownItem: { padding: '8px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' },
  itemRow: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
  qtyInput: { width: '60px', padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' },
  removeItemBtn: { padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  addItemBtn: { padding: '6px 12px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', marginBottom: '0.75rem' },
  confirmAddBtn: { width: '100%', padding: '10px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  confirmAddBtn: { width: '100%', padding: '10px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  productSearchBox: { background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '12px', padding: '1rem', marginBottom: '1rem' },
  productSearchLabel: { fontSize: '13px', color: '#92400e', marginBottom: '0.5rem', fontWeight: '500' },
  productSearchRow: { display: 'flex', gap: '0.5rem' },
  productSearchInput: { flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #fcd34d', fontSize: '14px' },
  productSearchBtn: { padding: '10px 20px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  productResultsBox: { marginTop: '1rem', background: '#fff', borderRadius: '8px', padding: '0.75rem' },
  productResultItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: '13px' },
  smartSearchBox: { marginBottom: '1rem' },
};

export default Invoices;